import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import './PrivacyPolicy.css';

const PrivacyPolicy = ({ embedded = false }) => {
  const navigate = useNavigate();

  const content = (
    <>
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last updated: 30 September 2026</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            This Privacy Policy explains how One to Nineteen ("we", "us", or "our") collects,
            uses, and protects your personal data when you use the 1-19 Golf application and
            website at{' '}
            <a href="https://1to19.com" target="_blank" rel="noopener noreferrer">https://1to19.com</a>{' '}
            (the "Service").
          </p>
          <p>
            We are committed to protecting your privacy in accordance with the UK General Data
            Protection Regulation (UK GDPR) and the Data Protection Act 2018. One to Nineteen
            is the data controller for the personal data processed through the Service.
          </p>
        </section>

        <section>
          <h2>2. Data We Collect</h2>
          <p>We collect the following categories of personal data:</p>

          <h3>Account Information</h3>
          <ul>
            <li>Full name</li>
            <li>Email address</li>
            <li>Password (stored securely by Firebase Authentication; we do not have access to your plaintext password)</li>
          </ul>

          <h3>Golf Data</h3>
          <ul>
            <li>Golf scores and hole-by-hole results</li>
            <li>Handicap information</li>
            <li>Tournament entries and results</li>
            <li>Leaderboard positions</li>
          </ul>

          <h3>Society Membership</h3>
          <ul>
            <li>Which golf societies you belong to</li>
            <li>Your role within each society (e.g. member, admin)</li>
          </ul>

          <h3>Shot Statistics (optional)</h3>
          <ul>
            <li>
              Hole-by-hole detail of your own shots &mdash; driving, approach, short game and
              putting &mdash; where you choose to record it. This is optional and can be switched
              off in the app at any time.
            </li>
          </ul>

          <h3>Scorecard Photographs (optional)</h3>
          <ul>
            <li>
              Photographs of scorecards you choose to scan, so that the scores written on them
              can be read automatically. See "Third-Party Services" below for who processes these.
            </li>
          </ul>

          <h3>Subscription Information</h3>
          <ul>
            <li>Your subscription status, plan, and whether it is due to renew</li>
            <li>Purchase and renewal records received from Apple or Google</li>
            <li>
              An anonymous identifier linking your account to our subscription provider. We do
              not send your name or email address to them.
            </li>
          </ul>

          <h3>Technical Data</h3>
          <ul>
            <li>Device type and browser information (collected automatically)</li>
            <li>IP address (processed by our hosting provider)</li>
            <li>Usage patterns within the Service</li>
            <li>
              A record of when you open the app: the date, whether you used the iOS app, the
              Android app or a browser, the operating system, and which version of the app you
              are running. These records do not include your IP address.
            </li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Your Data</h2>
          <p>We use your personal data for the following purposes:</p>
          <ul>
            <li><strong>Providing the Service:</strong> To create and manage your account, record scores, calculate handicaps, and display leaderboards.</li>
            <li><strong>Society Management:</strong> To enable you to create, join, and participate in golf societies.</li>
            <li><strong>Communication:</strong> To send you important updates about the Service (e.g. password resets, service changes).</li>
            <li><strong>Improvement:</strong> To understand how the Service is used and make improvements.</li>
            <li><strong>Security:</strong> To protect the Service and its users from unauthorised access or misuse.</li>
          </ul>
        </section>

        <section>
          <h2>4. Legal Basis for Processing</h2>
          <p>We process your personal data on the following legal bases under UK GDPR:</p>
          <ul>
            <li>
              <strong>Consent (Article 6(1)(a)):</strong> When you create an account and agree to
              these terms, you consent to the processing of your data as described in this policy.
            </li>
            <li>
              <strong>Contract Performance (Article 6(1)(b)):</strong> Processing is necessary to
              provide you with the Service you have signed up for.
            </li>
            <li>
              <strong>Legitimate Interests (Article 6(1)(f)):</strong> We have a legitimate interest
              in improving the Service, ensuring its security, and preventing misuse.
            </li>
          </ul>
        </section>

        <section>
          <h2>5. Third-Party Services</h2>
          <p>We use the following third-party services to operate the platform:</p>
          <ul>
            <li>
              <strong>Firebase Authentication (Google):</strong> Handles user authentication
              (sign-up, login, password reset). Google processes your email and authentication
              credentials. See{' '}
              <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">
                Firebase Privacy Information
              </a>.
            </li>
            <li>
              <strong>Render:</strong> Our backend infrastructure and database are hosted
              on Render. See{' '}
              <a href="https://render.com/privacy" target="_blank" rel="noopener noreferrer">
                Render Privacy Policy
              </a>.
            </li>
            <li>
              <strong>Google Gemini (Google):</strong> When you choose to scan a scorecard, the
              photograph you take is sent to Google's Gemini API so that the scores written on it
              can be read. The image is processed for that purpose only. Do not photograph
              anything you would not want processed this way. See{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Google Privacy Policy
              </a>.
            </li>
            <li>
              <strong>RevenueCat:</strong> Manages subscriptions and entitlements for the iOS and
              Android apps. It receives an anonymous app user identifier and the purchase records
              that Apple or Google send it. We deliberately do not send your name or email
              address to RevenueCat. See{' '}
              <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer">
                RevenueCat Privacy Policy
              </a>.
            </li>
          </ul>
          <p>
            We do not sell your personal data to any third party. We do not share your data with
            third parties for marketing purposes.
          </p>
        </section>

        <section>
          <h2>6. Data Storage and Security</h2>
          <p>
            Your data is stored on secure servers provided by Render. We implement
            appropriate technical and organisational measures to protect your data, including:
          </p>
          <ul>
            <li>Encrypted data transmission (HTTPS/TLS)</li>
            <li>Secure password handling via Firebase Authentication</li>
            <li>Access controls and authentication tokens</li>
            <li>Regular security reviews</li>
          </ul>
          <p>
            While we take reasonable steps to protect your data, no system is completely secure.
            We cannot guarantee absolute security of your information.
          </p>
        </section>

        <section>
          <h2>7. Data Retention</h2>
          <p>
            We retain your personal data for as long as your account is active or as needed to
            provide the Service. If you request account deletion, we will remove your personal
            data within 30 days, except where we are required to retain it for legal or
            regulatory purposes.
          </p>
          <p>
            Golf scores and tournament results may be retained in anonymised form for the
            purposes of maintaining society records and leaderboard history.
          </p>
        </section>

        <section>
          <h2>8. Your Rights</h2>
          <p>
            Under UK GDPR, you have the following rights regarding your personal data:
          </p>
          <ul>
            <li><strong>Right of Access:</strong> You can request a copy of the personal data we hold about you.</li>
            <li><strong>Right to Rectification:</strong> You can ask us to correct inaccurate or incomplete data.</li>
            <li><strong>Right to Erasure:</strong> You can request deletion of your personal data (subject to legal obligations).</li>
            <li><strong>Right to Restrict Processing:</strong> You can ask us to limit how we use your data.</li>
            <li><strong>Right to Data Portability:</strong> You can request your data in a structured, commonly used format.</li>
            <li><strong>Right to Object:</strong> You can object to processing based on legitimate interests.</li>
            <li><strong>Right to Withdraw Consent:</strong> Where processing is based on consent, you can withdraw it at any time.</li>
          </ul>
          <p>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:1to19golf@gmail.com">1to19golf@gmail.com</a>. We will respond to your
            request within one month, as required by UK GDPR.
          </p>
        </section>

        <section>
          <h2>9. Cookies and Local Storage</h2>
          <p>
            The Service uses browser local storage and IndexedDB to store authentication tokens
            and cache data for offline functionality. These are essential for the Service to
            operate and are not used for tracking or advertising purposes.
          </p>
          <p>
            We do not use third-party advertising or analytics cookies. Firebase Authentication
            may set cookies necessary for the authentication process.
          </p>
        </section>

        <section>
          <h2>10. Children's Privacy</h2>
          <p>
            The Service is not intended for children under 16 years of age. We do not knowingly
            collect personal data from children under 16. If we become aware that we have
            collected data from a child under 16, we will take steps to delete that information.
          </p>
        </section>

        <section>
          <h2>11. International Data Transfers</h2>
          <p>
            Some of our third-party service providers (such as Google Firebase) may process data
            outside the UK. Where this occurs, we ensure appropriate safeguards are in place, such
            as Standard Contractual Clauses or adequacy decisions, to protect your data in
            accordance with UK GDPR.
          </p>
        </section>

        <section>
          <h2>12. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. If we make significant changes,
            we will notify you via the Service or by email. The "Last updated" date at the top of
            this page indicates when this policy was last revised.
          </p>
        </section>

        <section>
          <h2>13. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or wish to exercise your data
            rights, please contact us at:
          </p>
          <p className="legal-contact">
            One to Nineteen<br />
            Email: <a href="mailto:1to19golf@gmail.com">1to19golf@gmail.com</a><br />
            Website: <a href="https://1to19.com" target="_blank" rel="noopener noreferrer">https://1to19.com</a>
          </p>
          <p>
            You also have the right to lodge a complaint with the Information Commissioner's
            Office (ICO) if you believe your data protection rights have been violated:{' '}
            <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">https://ico.org.uk</a>
          </p>
        </section>
    </>
  );

  if (embedded) return content;

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button className="legal-back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft size={18} />
          <span>Back</span>
        </button>
        {content}
      </div>
    </div>
  );
};

export default PrivacyPolicy;
